import { FC, useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import CreateLinkNumberInput from './CreateLinkNumberInput'
import {
  CRM_LINK_UNFIXED_DISCOUNT_MODE_OPTIONS,
  type CrmLinkDiscountMode,
} from './discountMode.utils'
import {
  CREATE_LINK_BLOCK_TARIFF_TYPES,
} from './tariffTypes'
import {
  withCreateLinkBlockTariffTypesHint,
} from './createLinkBlockInfoContent'
import spclasses from '../SetCourseClassDirectionModal/purchase.module.css'

type CourseDiscountModeFieldsProps = {
  value?: number | null
  onChange?: (value: number | null) => void
  discountMode: CrmLinkDiscountMode | null
  onDiscountModeChange: (mode: CrmLinkDiscountMode | null) => void
  /**
   * first_payment — верхнее поле без фиксации (first_only / every_payment).
   * renewal_only — поле над «Итого» при фиксации периода.
   */
  variant: 'first_payment' | 'renewal_only'
  /** Оставляем в API пропсов — вызывающие места передают. */
  fullBasePrice: number
  title?: string
}

/**
 * Скидка + режим для month / stream_custom / class.
 */
const CourseDiscountModeFields: FC<CourseDiscountModeFieldsProps> = ({
  value,
  onChange,
  discountMode,
  onDiscountModeChange,
  variant,
  title,
}) => {
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setInputValue(value != null && value > 0 ? String(value) : '')
  }, [value])

  const discountValue = useMemo(() => {
    const fromInput = Number(inputValue)
    if (Number.isFinite(fromInput) && fromInput > 0) return Math.trunc(fromInput)
    return Math.max(0, Math.trunc(Number(value) || 0))
  }, [inputValue, value])

  const effectiveMode: CrmLinkDiscountMode | null =
    discountValue > 0
      ? variant === 'renewal_only'
        ? 'renewal_only'
        : discountMode === 'every_payment'
        ? 'every_payment'
        : 'first_only'
      : null

  const handleDiscountChange = (next: number | null) => {
    const nextDiscount =
      next == null || !Number.isFinite(next) || next <= 0
        ? null
        : Math.trunc(next)
    setInputValue(nextDiscount != null ? String(nextDiscount) : '')
    onChange?.(nextDiscount)
    if (!nextDiscount) {
      onDiscountModeChange(null)
      return
    }
    if (variant === 'renewal_only') {
      onDiscountModeChange('renewal_only')
      return
    }
    onDiscountModeChange(
      discountMode === 'every_payment' ? 'every_payment' : 'first_only',
    )
  }

  const fieldTitle =
    title ??
    (variant === 'renewal_only' ? 'Скидка на продление, руб.' : 'Скидка, руб.')

  return (
    <div>
      <div className={spclasses.inputWithLabel}>
        <CreateLinkNumberInput
          className={spclasses.input}
          title={fieldTitle}
          integer
          min={0}
          placeholder="0"
          value={value}
          onChange={handleDiscountChange}
        />
        {variant === 'renewal_only' ? (
          <div className={spclasses.tariffTypeHint} style={{ marginTop: 4 }}>
            Скидка применяется только к продлениям. Первая оплата — сумма в
            «Итого к оплате».
          </div>
        ) : null}
      </div>

      {variant === 'first_payment' && discountValue > 0 ? (
        <div className={spclasses.inputWithLabel}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginBottom: 4,
            }}
          >
            <div className={spclasses.inputLabel} style={{ marginBottom: 0 }}>
              Режим скидки
            </div>
            <InfoComp
              text={withCreateLinkBlockTariffTypesHint(
                'Куда применяется скидка: только на первую оплату или на каждую (включая продления).',
                CREATE_LINK_BLOCK_TARIFF_TYPES.monthlyDiscount,
              )}
            />
          </div>
          <Select
            className={spclasses.selectField}
            popupClassName={spclasses.selectFieldPopup}
            value={effectiveMode ?? 'first_only'}
            options={CRM_LINK_UNFIXED_DISCOUNT_MODE_OPTIONS}
            onChange={(mode: CrmLinkDiscountMode) =>
              onDiscountModeChange(mode)
            }
          />
        </div>
      ) : null}
    </div>
  )
}

export default CourseDiscountModeFields
